import { AfterViewInit, Component, ComponentRef, ElementRef, HostListener, Input, ViewChild, ViewContainerRef } from "@angular/core";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { WindowScrollEvent } from "@ponder-core/events/ui.events";
import { FilteredPreviewItems } from "@ponder-shared/tools/filter/filtered-preview-items";
import { getScreenBreakpoint, ScreenBreakpoint } from "@ponder-shared/utils/screenBreakpoints";
import { Subscription } from "rxjs";
import { VirtualGridRowComponent } from "./virtual-grid-row.component";
import { VirtualGridOptions } from "./virtual-grid-options";
import { PreviewItemContextMenuComponet as PreviewItemContextMenuComponent } from "@ponder-shared/previewItem/preview-item-context-menu.component";
import { ItemSizeService } from "@ponder-shared/previewItem/item-size.service";
import { ToggleControlVisibilityComponent } from "@ponder-shared/components/toggle-control-visibility.component";

@Component({
    selector: "virtual-grid",
    imports: [PreviewItemContextMenuComponent, ToggleControlVisibilityComponent],
    templateUrl: "./virtual-grid.component.html"
})
export class VirtualGridComponent implements AfterViewInit {
    @Input({ required: true }) items!: FilteredPreviewItems;
    @Input({}) options: VirtualGridOptions = {}

    @ViewChild("virtualGrid") virtualGridRef!: ElementRef<HTMLElement>;
    @ViewChild("virtualGridRows", { read: ViewContainerRef }) private virtualGridRowsVcr!: ViewContainerRef;

    private windowResizeEventSub!: Subscription;
    private windowScrollEventSub!: Subscription;
    private itemVisibilityEventSub!: Subscription;

    private currentBreakpoint: ScreenBreakpoint = ScreenBreakpoint.Mobile;

    public readonly maxVisibleItemCount: number = 40;
    public rowCount: number = 10;
    public colCount: number = 2;
    private maxRowCount: number = 0;

    private itemWidth: number = 0;
    private itemHeight: number = 0;
    private virtualRows: ComponentRef<VirtualGridRowComponent>[] = [];

    constructor(events: EventBroker, private itemSizeService: ItemSizeService) {
        this.windowResizeEventSub = events.ui.windowResizeEvent.subscribe({
            next: () => {
                this.onWindowResizeEvent();
            }
        });

        this.windowScrollEventSub = events.ui.windowScrollEvent.subscribe({
            next: (event: WindowScrollEvent) => {
                this.onScrollEvent(event);
            }
        });

        this.itemVisibilityEventSub = events.ui.itemVisibilityRecalculatedEvent.subscribe({
            next: () => {
                this.onWindowResizeEvent();
            }
        });
    }

    public ngOnDestroy(): void {
        this.windowResizeEventSub.unsubscribe();
        this.windowScrollEventSub.unsubscribe();
        this.itemVisibilityEventSub.unsubscribe();
    }

    public ngAfterViewInit(): void {
        this.onWindowResizeEvent();
    }

    private onWindowResizeEvent(): void {
        this.setColumnRowCount();
        this.resizeGridContainer();
        this.rebuildGrid();
        this.recalculateGrid(true);
    }

    private onScrollEvent(event: WindowScrollEvent): void {
        if (event.scrolledUp || event.scrolledDown) {
            this.recalculateGrid();
        }
    }
    
    private setColumnRowCount(): void {
        var screenSize = getScreenBreakpoint();

        if (screenSize == this.currentBreakpoint) {
            return;
        }

        this.currentBreakpoint = screenSize;

        switch (screenSize) {
            case ScreenBreakpoint.Mobile:
            case ScreenBreakpoint.Small:
            case ScreenBreakpoint.Medium:
                this.colCount = 2;
                break;

            case ScreenBreakpoint.Large:
            case ScreenBreakpoint.XLarge:
                this.colCount = 3;
                break;

            case ScreenBreakpoint.XXLarge:
                this.colCount = 4;
                break;
        }

        this.rowCount = Math.trunc(this.maxVisibleItemCount / this.colCount);
    }

    private resizeGridContainer(): void {
        this.itemWidth = this.itemSizeService.width + 4;
        this.itemHeight = this.itemSizeService.height + 4;

        this.maxRowCount = Math.ceil(this.items.visibleItems.length / this.colCount);
        var totalHeight = this.maxRowCount * this.itemHeight;
        var totalWidth = this.colCount * this.itemWidth;

        this.virtualGridRef.nativeElement.style.setProperty("width", `${totalWidth}px`);
        this.virtualGridRef.nativeElement.style.setProperty("height", `${totalHeight}px`);
    }

    // -------------------------------------------------------------------------------------
    // --- Rebuild Grid
    //
    // Rebuilding the grid handles resizing the grid, and allocating/removing rows.
    // -------------------------------------------------------------------------------------

    private rebuildGrid(): void {
        this.resizeVirtualGrid();
        this.populateRows();
    }

    private resizeVirtualGrid(): void {
        // Add rows
        while (this.rowCount > this.virtualRows.length) {
            var rowRef = this.virtualGridRowsVcr.createComponent(VirtualGridRowComponent);
            rowRef.instance.options = this.options;
            
            this.virtualRows.push(rowRef);
        }

        // Remove extra rows
        while (this.rowCount < this.virtualRows.length) {
            this.virtualRows.pop();
            this.virtualGridRowsVcr.remove();
        }
    }

    private populateRows(): void {
        for (let i = 0; i < this.rowCount; ++i) {
            var row = this.virtualRows[i].instance;
            var start = i * this.colCount;
            var end = start + this.colCount;

            row.relativeRowIndex = i;
            row.updateCurrentRow(i, this.itemHeight);
            row.items = this.items.visibleItems.slice(start, end);

            row.triggerRebuild();
        }
    }
    
    // -------------------------------------------------------------------------------------
    // --- Recalculate Grid
    //
    // Recalculating the grid handles determining which rows are visible and assigning them items.
    // -------------------------------------------------------------------------------------

    private lastFirstRowIndex: number = 0;

    private recalculateGrid(force: boolean = false): void {
        if (this.itemHeight == 0) {
            return;
        }

        var gridBounds = this.virtualGridRef.nativeElement.getBoundingClientRect();
        var relativeY = (gridBounds.top >= 0 ? 0 : Math.abs(gridBounds.top));
        var firstRowIndex = Math.floor(relativeY / this.itemHeight);

        if (!force && this.lastFirstRowIndex == firstRowIndex) {
            // Didn't scroll enough to reveal/hide rows.
            return;
        }

        // If we have scrolled enough, we recalculate the index of every row.
        // We don't want to increment/decrement based on movement or else it will break down when making large skips.

        for (let i = 0; i < this.rowCount; ++i) {
            var row = this.virtualRows[i].instance;

            // You name something a, b, etc. when you don't know what to call it but you need it in the calculation ....
            var a = Math.max(0, firstRowIndex - i);
            var b = Math.ceil(a / this.rowCount);

            var currRowIndex = (b * this.rowCount) + row.relativeRowIndex;

            if (force || ((currRowIndex != row.currRowIndex) && (currRowIndex >= 0) && (currRowIndex < this.maxRowCount)))
            {
                let firstItem = currRowIndex * this.colCount;
                let lastItem = firstItem + this.colCount;

                row.items = this.items.visibleItems.slice(firstItem, lastItem);
                row.triggerRebuild();
                row.updateCurrentRow(currRowIndex, this.itemHeight, force);
            }
        }

        this.lastFirstRowIndex = firstRowIndex;
    }
}